import { damagePlayer } from './player';
import { playSpiderDeathSound } from './audio';

// Frame layout (17×16 per frame, 5 frames):
// 0: Standard (hanging), 1: Dive Attack, 2: Damage, 3: Dead, 4: Web segment
const FRAMES = { standard: 0, dive: 1, damage: 2, dead: 3, web: 4 };

const IDLE_AMPLITUDE = 15;    // px of up/down oscillation
const IDLE_SPEED     = 0.001; // radians per ms
const DIVE_SPEED     = 150;
const ASCEND_SPEED   = 50;
const MAX_DIVE_DEPTH = 220;
const PROXIMITY_X    = 70;
const MAX_WEB_SEGS   = 16;    // max 16 × 16px = 256px of web

const CONFIGS = [
  { x: 80,   anchorY: 24  }, // outer wall above entry door
  { x: 1104, anchorY: 24  }, // guarding 1up
];

export function createSpiders(scene) {
  scene.spiders = [];

  scene.anims.create({
    key: 'spiderDie',
    frames: [{ key: 'spider', frame: FRAMES.dead }],
    frameRate: 1,
    repeat: 0
  });

  for (const cfg of CONFIGS) addSpider(scene, cfg.x, cfg.anchorY);
}

function addSpider(scene, x, anchorY) {
  const spider = scene.physics.add.sprite(x, anchorY, 'spider', FRAMES.standard);
  spider.setScale(1);
  spider.body.setAllowGravity(false);
  spider.body.setSize(12, 14);
  spider.body.setOffset(2, 1);
  spider.setDepth(5);

  spider.health        = 1;
  spider.state         = 'idle';
  spider.anchorX       = x;
  spider.anchorY       = anchorY;
  spider.isDying       = false;
  spider.idleStartTime = 0;
  spider.isVulnerable  = false;

  // Web built from the sprite's own web frame (frame 4), stacked in 16px segments
  spider.webSegments = [];
  const webTopY = anchorY - 8;
  for (let i = 0; i < MAX_WEB_SEGS; i++) {
    const seg = scene.add.image(x, webTopY + i * 16, 'spider', FRAMES.web);
    seg.setOrigin(0.5, 0);
    seg.setVisible(false);
    seg.setDepth(4);
    spider.webSegments.push(seg);
  }

  // Collider with environment — only active during dive so it doesn't fight idle positioning
  spider.envCollider = scene.physics.add.collider(spider, scene.environmentLayer);
  spider.envCollider.active = false;

  spider.hit = (facing) => hitSpider(scene, spider, facing);

  scene.physics.add.overlap(scene.player, spider, () => {
    if (spider.isDying) return;
    const alreadyInvincible = scene.playerInvincible;
    damagePlayer(scene, 0.5);
    if (scene.player.isDead) return;
    if (alreadyInvincible) return;
    if (scene.player.isClimbing || scene.player.isPerched) return;
    const knockDir = scene.player.x < spider.x ? -1 : 1;
    scene.player.setVelocityX(knockDir * 180);
    scene.player.knockbackTimer = 300;
  }, null, scene);

  scene.spiders.push(spider);
}

function hitSpider(scene, spider, _facing) {
  if (spider.isDying) return;
  spider.health -= 1;
  spider.setFrame(FRAMES.damage);
  scene.time.delayedCall(500, () => {
    if (!spider?.active || spider.isDying) return;
    spider.setFrame(spider.state === 'diving' ? FRAMES.dive : FRAMES.standard);
  });
  if (spider.health <= 0) killSpider(scene, spider);
}

function killSpider(scene, spider) {
  spider.isDying = true;
  playSpiderDeathSound();
  spider.webSegments.forEach(seg => seg.destroy());
  spider.webSegments = [];
  spider.setFrame(FRAMES.dead);
  // Fall to the floor then vanish
  spider.body.setAllowGravity(true);
  spider.envCollider.active = true;
  scene.time.delayedCall(800, () => spider?.destroy());
}

export function updateSpiders(scene, time) {
  for (const spider of scene.spiders) {
    if (!spider.active) continue;
    if (spider.isDying) continue;

    // Update web segments: full segments + one partial to fill gap smoothly
    const totalLength = Math.max(0, spider.y - spider.anchorY);
    const fullSegs = Math.floor(totalLength / 16);
    const remainder = totalLength % 16;
    spider.webSegments.forEach((seg, i) => {
      if (i < fullSegs) {
        seg.setVisible(true).setScale(1, 1);
      } else if (i === fullSegs && remainder > 0) {
        seg.setVisible(true).setScale(1, remainder / 16);
      } else {
        seg.setVisible(false);
      }
    });

    if (spider.state === 'idle') {
      spider.envCollider.active = false;
      const phase = (time - spider.idleStartTime) * IDLE_SPEED;
      spider.body.reset(spider.anchorX, spider.anchorY + (1 - Math.cos(phase)) / 2 * IDLE_AMPLITUDE);

      const dx = Math.abs(scene.player.x - spider.x);
      if (dx < PROXIMITY_X && scene.player.y > spider.y) {
        spider.state = 'diving';
        spider.isVulnerable = true;
        spider.setFrame(FRAMES.dive);
        spider.envCollider.active = true;
      }
    } else if (spider.state === 'diving') {
      spider.setVelocityY(DIVE_SPEED);
      if (spider.body.blocked.down || spider.y >= spider.anchorY + MAX_DIVE_DEPTH) {
        spider.state = 'ascending';
        spider.setFrame(FRAMES.standard);
        spider.envCollider.active = false;
      }
    } else if (spider.state === 'ascending') {
      spider.setVelocityY(-ASCEND_SPEED);
      if (spider.y <= spider.anchorY) {
        spider.body.reset(spider.anchorX, spider.anchorY);
        spider.setVelocityY(0);
        spider.idleStartTime = time;
        spider.isVulnerable = false;
        spider.state = 'idle';
        spider.setFrame(FRAMES.standard);
      }
    }
  }

  scene.spiders = scene.spiders.filter(s => s.active);
}
